import { useEffect, useState } from "react";
import axios from "axios";
import { FiUsers, FiHeart } from "react-icons/fi";
import UserGrowthChart from "../components/Charts/UserGrowthChart";
import { FaFemale, FaMale } from "react-icons/fa";
import { IoStatsChart } from "react-icons/io5";
import { MdOutlineAutoGraph } from "react-icons/md";

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    newUsersThisMonth: 0,
  });
  const [count, setcount] = useState();
  const [recentUsers, setRecentUsers] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [years] = useState([currentYear, currentYear - 1, currentYear - 2]);

  useEffect(() => {
    fetchDashboardData(selectedYear);
  }, [selectedYear]);

  const fetchDashboardData = async (year = selectedYear) => {
    try {
      setLoading(true);

      const [analyticsRes, usersRes, growthRes, genderCount] = await Promise.all([
        axios.get('/api/v1/admin/analytics/dashboard'),
        axios.get('/api/v1/admin/users'),
        axios.get(`/api/v1/admin/analytics/users?year=${year}`),
        axios.get(`/api/v1/user/getGender`)
      ]);

      if (analyticsRes.data.success) {
        setStats(analyticsRes.data.data);
      }
      if (genderCount.data.success) {
        setcount(genderCount.data.data);
      }

      if (usersRes.data.success) {
        const users = usersRes.data.data || [];
        setRecentUsers(users.slice(0, 5));
      }

      if (growthRes.data.success) {
        setUserGrowth(growthRes.data.data.monthlyGrowth || []);
      }
    } catch (error) {
      console.error("Dashboard Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-row">
        <div className="spinner w-12 h-12" />
      </div>
    );
  }

  const filteredUsers =
    activeTab === "verified"
      ? recentUsers.filter((user) => user.isProfileCompleted)
      : recentUsers;

  const statCards = [
    {
      label: "Total Users",
      value: stats.totalUsers,
      icon: <FiUsers />,
      accent: "blue",
      from: "rgba(59,130,246,0.2)",
      to: "rgba(59,130,246,0.03)",
      ring: "rgba(59,130,246,0.25)",
      color: "#60a5fa",
      glow: "rgba(59,130,246,0.1)",
    },
    {
      label: "Active Users",
      value: stats.activeUsers,
      icon: <FiHeart />,
      accent: "emerald",
      from: "rgba(16,185,129,0.2)",
      to: "rgba(16,185,129,0.03)",
      ring: "rgba(16,185,129,0.25)",
      color: "#34d399",
      glow: "rgba(16,185,129,0.1)",
    },
    {
      label: "Male Users",
      value: count?.male || 0,
      icon: <FaMale />,
      accent: "violet",
      from: "rgba(139,92,246,0.2)",
      to: "rgba(139,92,246,0.03)",
      ring: "rgba(139,92,246,0.25)",
      color: "#a78bfa",
      glow: "rgba(139,92,246,0.1)",
    },
    {
      label: "Female Users",
      value: count?.female || 0,
      icon: <FaFemale />,
      accent: "pink",
      from: "rgba(236,72,153,0.2)",
      to: "rgba(236,72,153,0.03)",
      ring: "rgba(236,72,153,0.25)",
      color: "#f472b6",
      glow: "rgba(236,72,153,0.1)",
    },
  ];

  return (
    <div className="space-y-6 text-[var(--text-primary)]">
      {/* HEADER */}
      <div className="page-header">
        <div className="page-title-wrap">
          <h1 className="txt-display">Dashboard Overview</h1>
          <p className="page-subtitle">Monitor key metrics, user growth and recent activity</p>
        </div>
        <div className="page-actions">
          {/* <span className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full badge badge-primary badge-dot">
            Live data
          </span> */}
        </div>
      </div>

      {/* TOP STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {statCards.map((s) => (
          <div
            key={s.label}
            className="stat-card card-hover"
          >
            <div
              className="absolute inset-0 pointer-events-none rounded-2xl"
              style={{
                background: `linear-gradient(135deg, ${s.from}, ${s.to})`,
                opacity: 0.9,
              }}
            />
            <div
              className="absolute -top-10 -right-10 w-36 h-36 rounded-full pointer-events-none"
              style={{ background: `radial-gradient(circle, ${s.glow}, transparent 65%)` }}
            />
            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="txt-label mb-2">{s.label}</p>
                <p
                  className="font-bold tracking-tight"
                  style={{
                    fontSize: "2.15rem",
                    lineHeight: 1.1,
                    color: s.color,
                    textShadow: `0 1px 20px ${s.glow}`,
                  }}
                >
                  {Number(s.value || 0).toLocaleString()}
                </p>
              </div>
              <div
                className="stat-icon-wrap flex-shrink-0"
                style={{
                  background: `linear-gradient(135deg, ${s.from}, ${s.to})`,
                  color: s.color,
                  border: `1px solid ${s.ring}`,
                  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), 0 0 24px ${s.glow}`,
                }}
              >
                {s.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* USER GROWTH + ACTIVITY */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* User Growth */}
        <div className="card card-padding lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
            <div className="flex items-center gap-3">
              <div
                className="stat-icon-wrap w-10 h-10 rounded-lg text-lg"
                style={{
                  background: "rgba(168,85,247,0.14)",
                  border: "1px solid rgba(168,85,247,0.28)",
                  color: "#c084fc",
                }}
              >
                <MdOutlineAutoGraph />
              </div>
              <div>
                <h2 className="txt-h3" style={{ fontSize: "1.1rem" }}>User Growth</h2>
                <p className="txt-caption">Monthly new registrations</p>
              </div>
            </div>
            <div className="min-w-[140px]">
              <label className="form-label sr-only">Year</label>
              <select
                value={selectedYear}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setSelectedYear(y);
                  fetchDashboardData(y);
                }}
                className="select"
              >
                {years.map((yr) => (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="min-h-[300px]">
            <UserGrowthChart rawGrowth={userGrowth} selectedYear={selectedYear} />
          </div>
        </div>

        {/* User Activity */}
        <div className="card card-padding">
          <div className="flex items-center gap-3 mb-5">
            <div
              className="stat-icon-wrap w-10 h-10 rounded-lg text-lg"
              style={{
                background: "rgba(6,182,212,0.14)",
                border: "1px solid rgba(6,182,212,0.28)",
                color: "#22d3ee",
              }}
            >
              <IoStatsChart />
            </div>
            <div>
              <h2 className="txt-h3" style={{ fontSize: "1.1rem" }}>User Activity</h2>
              <p className="txt-caption">Engagement snapshot</p>
            </div>
          </div>

          <div className="space-y-3.5">
            {[
              {
                label: "Active Users Today",
                value: stats.activeUsers,
                accent: "#34d399",
                from: "rgba(16,185,129,0.12)",
                to: "rgba(16,185,129,0.02)",
              },
              {
                label: "Total Users",
                value: stats.totalUsers,
                accent: "#60a5fa",
                from: "rgba(59,130,246,0.12)",
                to: "rgba(59,130,246,0.02)",
              },
              {
                label: "New Users This Month",
                value: stats.newUsersThisMonth != null ? `+${stats.newUsersThisMonth}` : "+0",
                accent: "#a78bfa",
                from: "rgba(139,92,246,0.12)",
                to: "rgba(139,92,246,0.02)",
              },
              {
                label: "New Users Today",
                value: stats.newUsersToday != null ? `+${stats.newUsersToday}` : "+0",
                accent: "#f472b6",
                from: "rgba(236,72,153,0.12)",
                to: "rgba(236,72,153,0.02)",
              },
            ].map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between rounded-xl p-3.5"
                style={{
                  background: `linear-gradient(135deg, ${row.from}, ${row.to})`,
                  border: "1px solid rgba(139,92,246,0.1)",
                }}
              >
                <span className="text-sm" style={{ color: "#c4bad8" }}>
                  {row.label}
                </span>
                <span
                  className="font-bold text-lg"
                  style={{ color: row.accent }}
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RECENT USERS */}
      <div className="table-wrap mb-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4" style={{ borderBottom: "1px solid var(--border-default)" }}>
          <div className="flex items-center gap-3">
            <div
              className="stat-icon-wrap w-10 h-10 rounded-lg text-lg"
              style={{
                background: "rgba(236,72,153,0.14)",
                border: "1px solid rgba(236,72,153,0.28)",
                color: "#f472b6",
              }}
            >
              <FiUsers />
            </div>
            <div>
              <h2 className="txt-h3" style={{ fontSize: "1.1rem" }}>Recent Users</h2>
              <p className="txt-caption">Latest members to join the platform</p>
            </div>
          </div>

          <div className="tabs">
            <button
              onClick={() => setActiveTab("all")}
              className={`tab-btn ${activeTab === "all" ? "tab-active" : ""}`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab("verified")}
              className={`tab-btn ${activeTab === "verified" ? "tab-active" : ""}`}
            >
              Verified
            </button>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="table-empty">
            <FiUsers />
            <p className="font-medium" style={{ color: "var(--text-secondary)" }}>No users yet</p>
            <p className="txt-caption mt-1">Check back soon for new signups</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: "1.25rem" }}>USER</th>
                  <th>PROFILE COMPLETION</th>
                  <th>JOINED</th>
                  <th>GENDER</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const initials = (user.fullName || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
                  return (
                    <tr key={user._id || user.id}>
                      <td style={{ paddingLeft: "1.25rem" }}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="avatar avatar-md">{initials}</div>
                          <div className="min-w-0">
                            <p className="font-semibold text-[var(--text-primary)] truncate">{user.fullName}</p>
                            {/* <p className="text-xs text-[var(--text-muted)] truncate">{user.phoneNumber}</p> */}
                                                                            <div className="text-xs text-[var(--black-text)]/70">
  {(() => {
    const phone = String(user.phoneNumber || "").replace(/\D/g, "");

    // 10 digits → +91 XXXXX XXXXX
    if (phone.length === 10) {
      return `+91 ${phone.slice(0, 5)} ${phone.slice(5)}`;
    }

    // 12 digits starting with 91 → +91 XXXXX XXXXX
    if (phone.length === 12 && phone.startsWith("91")) {
      const number = phone.slice(2);
      return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
    }

    // Already has country code or other format
    return user.phoneNumber;
  })()}
</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-dot ${user.isProfileCompleted ? "badge-success" : "badge-danger"}`}>
                          {user.isProfileCompleted ? "Completed" : "Not Completed"}
                        </span>
                      </td>
                      <td>
                        <span className="text-sm" style={{ color: "var(--text-secondary)" }}>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              })
                            : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="badge badge-primary">{user.gender || "—"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="mt-5">

      </div>
    </div>
  );
}
